import type { WebhookDeliveryStatus } from '@monorepo/shared/schemas/common'
import type {
  CreateWebhook,
  PaginatedWebhooksResponse,
  TestWebhookResponse,
  UpdateWebhook,
  WebhookDeliveriesResponse,
  WebhookResponse,
} from '@monorepo/shared/schemas/webhooks'
import {
  PaginatedWebhooksResponseSchema,
  RetryDeliveryResponseSchema,
  TestWebhookResponseSchema,
  WebhookDeliveriesResponseSchema,
  WebhookResponseSchema,
} from '@monorepo/shared/schemas/webhooks'
import { del, get, post, put } from '@/lib/api-client'

export const webhookService = {
  async listWebhooks(params?: {
    page?: number
    limit?: number
    active?: boolean
    event_type?: string
    url_contains?: string
  }): Promise<PaginatedWebhooksResponse> {
    const query = new URLSearchParams()
    if (params?.page) query.append('page', String(params.page))
    if (params?.limit) query.append('limit', String(params.limit))
    if (params?.active !== undefined)
      query.append('active', String(params.active))
    if (params?.event_type) query.append('event_type', params.event_type)
    if (params?.url_contains) query.append('url_contains', params.url_contains)
    const qs = query.toString()
    return get(
      `/webhooks${qs ? `?${qs}` : ''}`,
      PaginatedWebhooksResponseSchema
    )
  },

  async getWebhook(id: string): Promise<WebhookResponse> {
    return get(`/webhooks/${id}`, WebhookResponseSchema)
  },

  async createWebhook(data: CreateWebhook): Promise<WebhookResponse> {
    return post('/webhooks', data, WebhookResponseSchema)
  },

  async updateWebhook(
    id: string,
    data: UpdateWebhook
  ): Promise<WebhookResponse> {
    return put(`/webhooks/${id}`, data, WebhookResponseSchema)
  },

  async deleteWebhook(id: string): Promise<void> {
    await del(`/webhooks/${id}`)
  },

  async getDeliveries(
    webhookId: string,
    params?: {
      page?: number
      limit?: number
      status?: WebhookDeliveryStatus
      event_type?: string
    }
  ): Promise<WebhookDeliveriesResponse> {
    const query = new URLSearchParams()
    if (params?.page) query.append('page', String(params.page))
    if (params?.limit) query.append('limit', String(params.limit))
    if (params?.status) query.append('status', params.status)
    if (params?.event_type) query.append('event_type', params.event_type)
    const qs = query.toString()
    return get(
      `/webhooks/${webhookId}/deliveries${qs ? `?${qs}` : ''}`,
      WebhookDeliveriesResponseSchema
    )
  },

  async testWebhook(
    webhookId: string,
    eventType?: string
  ): Promise<TestWebhookResponse> {
    return post(
      `/webhooks/${webhookId}/test`,
      { event_type: eventType },
      TestWebhookResponseSchema
    )
  },

  async retryDelivery(webhookId: string, deliveryId: string) {
    return post(
      `/webhooks/${webhookId}/retry/${deliveryId}`,
      undefined,
      RetryDeliveryResponseSchema
    )
  },
}
